import React, { useEffect, useState } from "react";
import { getUsersLight } from "../../api/user.api";
import { Box, Button, Divider, Stack, Typography, Grid } from "@mui/material";
import { useAuth } from "../../context/auth.context";
import { getActiveEvent } from "../../api/event.api.js";
import { useSnackbar } from "../../context/snackbar.context.js";
import { useWebSocket } from "../../context/websocket.context.js";
import {
  addGather,
  getGather,
  getMyGather,
  removeGather,
  setGatherStatus,
} from "../../api/gather.api.js";
import CreateGatherModal from "../../components/modals/createGather.modal.js";
import UserCard from "../../components/cards/user.card.js";
import { useLocation, useNavigate } from "react-router-dom";
import StatusChip from "../../components/chips/status.chip.js";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AttractionsIcon from "@mui/icons-material/Attractions";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

const GatherPage = () => {
  const auth = useAuth();
  const showSnackbar = useSnackbar();
  const websocket = useWebSocket();
  const location = useLocation();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [gather, setGather] = useState(location.state?.gather || null);
  const [participants, setParticipants] = useState([]);
  const [eventId, setEventId] = useState();
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    if (!websocket || !websocket.message) {
      return;
    }
    if (websocket.message.Type !== "Gather") {
      return;
    }
    if (websocket.message.Operation === 2) {
      return;
    }
    setRefresh((prev) => !prev);
  }, [websocket]);

  useEffect(() => {
    getActiveEvent().then((response) => {
      setEventId(response.data.id);
    });
  }, [refresh]);

  useEffect(() => {
    if (!eventId) {
      return;
    }
    if (!gather) {
      getMyGather(eventId)
        .then((response) => {
          setGather(response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      getGather(eventId, gather.id).then((response) => {
        setGather(response.data);
      });
    }
  }, [eventId, refresh]);

  useEffect(() => {
    if (!gather) {
      return;
    }
    getUsersLight(gather.participantIds).then((response) => {
      setParticipants(response.data);
    });
  }, [gather]);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleRemove = () => {
    removeGather(gather)
      .then(() => {
        showSnackbar("Gather removed!", "success");
        navigate("/");
      })
      .catch((error) => {
        showSnackbar("Failed to remove Gather!", "error");
      });
  };

  const handleNextStatus = () => {
    setGatherStatus(gather)
      .then(() => {
        showSnackbar("Gather started!", "success");
        if (gather.status === "Running") {
          navigate("/");
        }
      })
      .catch((error) => {
        showSnackbar("Failed to start Gather!", "error");
      });
  };

  const isOwner = () => {
    return gather && gather.ownerId === auth.user.id;
  };

  const handleSubmit = (formData) => {
    formData.eventId = eventId;
    formData.ownerId = auth.user.id;
    formData.participantIds = [];
    addGather(formData)
      .then(() => {
        showSnackbar("Gather created!", "success");
      })
      .catch((error) => {
        showSnackbar("Faield to create Gather!", "error");
      });
  };

  return (
    <div>
      <Typography variant="h3" gutterBottom>
        Gather
      </Typography>

      {isOwner() && (
        <Stack direction="row" spacing={1}>
          {
            <Button variant="contained" onClick={handleRemove}>
              Remove
            </Button>
          }
          {
            <Button variant="contained" onClick={handleNextStatus}>
              {gather.status === "Running" && "Close"}
              {gather.status !== "Running" && "Start"}
            </Button>
          }
        </Stack>
      )}

      <Divider sx={{ mt: 1, mb: 1 }} />

      {!gather && (
        <div>
          <Typography>You don't have a Gather, yet!</Typography>
          <Button variant="contained" onClick={handleOpenModal}>
            Create Gather
          </Button>
          <CreateGatherModal
            aria-modal
            open={openModal}
            handleClose={handleCloseModal}
            handleSubmit={handleSubmit}
            eventId={eventId}
          />
        </div>
      )}

      {gather && (
        <div>
          <Typography variant="h4" gutterBottom color="primary">
            {gather.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ color: "text.secondary", mr: 3 }}>
              {gather.game.name}
            </Typography>
            {gather.game.category === "Shooter" && (
              <CrisisAlertIcon sx={{ fontSize: 16, mr: 1 }} />
            )}
            {gather.game.category === "Racing" && (
              <DirectionsCarIcon sx={{ fontSize: 16, mr: 1 }} />
            )}
            {gather.game.category === "Strategy" && (
              <AccountBalanceIcon sx={{ fontSize: 16, mr: 1 }} />
            )}
            {gather.game.category === "Other" && (
              <AttractionsIcon sx={{ fontSize: 16, mr: 1 }} />
            )}
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {gather.game.category}
            </Typography>
          </Box>
          <StatusChip status={gather.status} />

          <Typography>
            Host:{" "}
            {
              participants.find(
                (participant) => participant.id == gather.ownerId,
              )?.nickname
            }
          </Typography>
          <Typography>{gather.description}</Typography>

          <Divider sx={{ mt: 1, mb: 1 }} />

          <Typography variant="h4" gutterBottom>
            Participants
          </Typography>
          <Grid container spacing={1}>
            {gather &&
              participants &&
              participants.length > 0 &&
              participants.map((participant) => (
                <Grid key={participant.nickname} xs={12} sm={6} md={4}>
                  <UserCard user={participant} />
                </Grid>
              ))}
          </Grid>
        </div>
      )}
    </div>
  );
};

export default GatherPage;
